import { ChatApiError, submitQuestion } from '@/services/chat-api';
import type {
  HelpAgentDraftAttachment,
  HelpAgentSubmitHandler,
  HelpAgentSubmitResult,
} from '@/types/attachments';
import type { ChatConfig } from '@/types/chat';

interface SubmitQuestionParams {
  config: ChatConfig;
  question: string;
  authToken: string | null;
  attachments: readonly HelpAgentDraftAttachment[];
  submitHandler: HelpAgentSubmitHandler | null;
}

export interface HelpAgentSubmissionError {
  inlineMessage: string;
  notificationMessage: string;
}

export class HelpAgentAttachmentSubmitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HelpAgentAttachmentSubmitError';
  }
}

export class HelpAgentSubmissionController {
  async submit({
    config,
    question,
    authToken,
    attachments,
    submitHandler,
  }: SubmitQuestionParams): Promise<HelpAgentSubmitResult> {
    if (submitHandler) {
      return submitHandler({
        config,
        token: authToken,
        question,
        attachments,
      });
    }

    if (attachments.length > 0) {
      throw new HelpAgentAttachmentSubmitError('Attachments are not supported by the configured backend yet.');
    }

    const response = await submitQuestion({
      config,
      question,
      authToken,
    });

    return {
      entityId: response.entity_id,
      sessionId: config.sessionId || null,
    };
  }

  getErrorDetails(error: unknown): HelpAgentSubmissionError {
    if (error instanceof HelpAgentAttachmentSubmitError) {
      return {
        inlineMessage: error.message,
        notificationMessage: 'Attachments are not supported yet',
      };
    }

    if (error instanceof ChatApiError && error.status === 401) {
      return {
        inlineMessage: 'Authorization required. Add a valid token and try again.',
        notificationMessage: 'Authorization required',
      };
    }

    return {
      inlineMessage: 'An error occurred while sending the message. Please try again.',
      notificationMessage: 'Send error',
    };
  }
}
